import { FC } from 'react';

import Accordion from 'dashboard/components/Accordion';
import FormError from 'dashboard/components/FormControls/FormError';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { useFormSecurely } from 'lib/security/reactHookForm';

export const MAX_EXPIRATION_SECONDS = 525600;

interface TriggerAdvancedConfigurationPanelProps {
  editable: boolean;
  workflowVersionExpiration: number;
  updateWorkflowVersionExpiration: (expiration: number) => void;
}

const TriggerAdvancedConfigurationPanel: FC<TriggerAdvancedConfigurationPanelProps> = ({
  editable,
  workflowVersionExpiration,
  updateWorkflowVersionExpiration,
}) => {
  const {
    formState: { errors },
    register,
    getValues,
  } = useFormSecurely<{ expiration: number }>({
    defaultValues: {
      expiration: workflowVersionExpiration,
    },
    mode: 'onChange',
  });

  return (
    <Accordion title='Advanced' defaultExpanded={false}>
      <Spacer height={1} />
      <form>
        <ParamLabel
          helpText='A Workflow Run can expire if the Workflow Run has not finished processing after the
        given duration. An expired Workflow Run will not process and will require manual
        intervention. Any in progress Workflow Runs could be interrupted after the expiration
        duration passes.'
          htmlFor='trigger-advanced-configuration_expiration'
          labelText='Time to expire after run creation (minutes)'
        />
        <Input
          id='trigger-advanced-configuration_expiration'
          disabled={!editable}
          hasError={!!errors.expiration}
          type='number'
          {...register('expiration', {
            onChange: () => {
              updateWorkflowVersionExpiration(getValues('expiration'));
            },
            required: 'Expiration must be set',
            valueAsNumber: true,
            min: {
              value: 0,
              message: 'Expiration must be non-negative',
            },
            max: {
              value: MAX_EXPIRATION_SECONDS,
              message: `Expiration must be less than or equal to ${MAX_EXPIRATION_SECONDS}`,
            },
          })}
        />
        {errors.expiration && <FormError>{errors.expiration.message}</FormError>}
      </form>
    </Accordion>
  );
};

export default TriggerAdvancedConfigurationPanel;
